import { useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
    Dialog, DialogContent, DialogDescription,
    DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Trash2, ShieldAlert, Mail, KeyRound, Info } from 'lucide-react';
import { toast } from 'sonner';
import { PasswordInput } from './PasswordInput';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';

/**
 * DeleteAccountPanel
 *
 * Two-step destructive delete:
 *  1) Init: email OTP is sent to registered address.
 *  2) Confirm: user enters OTP + account password.
 *
 * Second factor is currently password. Once Meta WhatsApp Cloud approval
 * completes, this will be swapped for a WhatsApp OTP.
 */
export function DeleteAccountPanel() {
    const { logout, user } = useAuth();
    const [dialogOpen, setDialogOpen] = useState(false);
    const [stage, setStage] = useState('idle'); // 'idle' | 'sent' | 'busy' | 'done'
    const [otp, setOtp] = useState('');
    const [password, setPassword] = useState('');
    const [ttlLeft, setTtlLeft] = useState(0);
    const [initInfo, setInitInfo] = useState(null);

    useEffect(() => {
        if (!ttlLeft) return;
        const t = setInterval(() => setTtlLeft((s) => Math.max(0, s - 1)), 1000);
        return () => clearInterval(t);
    }, [ttlLeft]);

    function reset() {
        setStage('idle');
        setOtp('');
        setPassword('');
        setTtlLeft(0);
        setInitInfo(null);
    }

    async function requestOtp() {
        setStage('busy');
        try {
            const r = await api.deleteAccountInit();
            setInitInfo(r);
            setTtlLeft(Math.max(0, Number(r?.ttl_seconds || 0)));
            setStage('sent');
            toast.success(
                r?.email_sent
                    ? `Code sent to ${r.email}`
                    : 'Code generated (email delivery failed — check spam/SMTP).',
            );
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Failed to send code');
            setStage('idle');
        }
    }

    async function confirmDelete() {
        if (!otp.trim() || !password) {
            toast.error('Enter both the code and your password.');
            return;
        }
        setStage('busy');
        try {
            const r = await api.deleteAccountConfirm({ otp: otp.trim(), password });
            if (r?.deleted) {
                setStage('done');
                toast.success('All your data has been permanently deleted.');
                // Sign the user out — their user record is gone.
                setTimeout(() => {
                    try { logout?.(); } catch { /* noop */ }
                    setDialogOpen(false);
                    window.location.href = '/setup';
                }, 1500);
            } else {
                setStage('sent');
                toast.error('Deletion did not complete. Please try again.');
            }
        } catch (e) {
            setStage('sent');
            toast.error(e?.response?.data?.detail || 'Confirmation failed');
        }
    }

    const mm = String(Math.floor(ttlLeft / 60)).padStart(2, '0');
    const ss = String(ttlLeft % 60).padStart(2, '0');

    return (
        <Card
            className="p-5 border-[hsl(var(--price-increase-border))]/50 bg-[hsl(var(--price-increase-bg))]/25"
            data-testid="delete-account-panel"
        >
            <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-lg bg-[hsl(var(--price-increase-bg))] text-[hsl(var(--price-increase-fg))] flex items-center justify-center shrink-0">
                    <ShieldAlert size={18} />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="font-heading text-lg text-[hsl(var(--price-increase-fg))]">
                        Delete all my data
                    </div>
                    <div className="text-xs text-muted-foreground">
                        Permanently erase everything. Irreversible.
                    </div>
                </div>
                <Button
                    variant="outline"
                    onClick={() => { reset(); setDialogOpen(true); }}
                    className="gap-2 border-[hsl(var(--price-increase-border))] text-[hsl(var(--price-increase-fg))]"
                    data-testid="delete-account-open-btn"
                >
                    <Trash2 size={15} /> Delete my data…
                </Button>
            </div>

            <Dialog open={dialogOpen} onOpenChange={(v) => { setDialogOpen(v); if (!v) reset(); }}>
                <DialogContent className="max-w-md" data-testid="delete-account-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-[hsl(var(--price-increase-fg))]">
                            <ShieldAlert size={18} /> Confirm deletion
                        </DialogTitle>
                        <DialogDescription>
                            Signed in as <span className="font-mono">{user?.email}</span>. This step cannot be undone.
                        </DialogDescription>
                    </DialogHeader>

                    {stage === 'idle' && (
                        <div className="space-y-3">
                            <Alert className="border-[hsl(var(--price-increase-border))] bg-[hsl(var(--price-increase-bg))] text-[hsl(var(--price-increase-fg))]">
                                <ShieldAlert className="h-4 w-4" />
                                <AlertTitle className="font-heading">Are you absolutely sure?</AlertTitle>
                                <AlertDescription className="text-xs">
                                    This wipes every bill, item, vendor, staff record, payment, alert,
                                    expense, opening balance, backup and setting from our database.
                                    Cannot be undone.
                                </AlertDescription>
                            </Alert>
                            <div className="text-xs text-muted-foreground space-y-1.5 px-1">
                                <div>· We require <strong>two factors</strong>: an email OTP sent to your registered address, plus your account password.</div>
                                <div>· Prefer to keep a copy? Cancel this dialog and use <strong>Backup &amp; restore</strong> above to download a JSON snapshot first.</div>
                                <div>· Once complete, you'll be signed out and can register fresh at any time.</div>
                            </div>
                            <Alert>
                                <Info className="h-4 w-4" />
                                <AlertTitle className="font-heading text-sm">Second factor</AlertTitle>
                                <AlertDescription className="text-xs">
                                    Currently your <strong>account password</strong>. Once WhatsApp Cloud API
                                    approval completes, we'll switch it to a WhatsApp OTP automatically.
                                </AlertDescription>
                            </Alert>
                        </div>
                    )}

                    {stage === 'sent' && (
                        <div className="space-y-4">
                            <div className="p-3 rounded-md bg-muted/40 text-xs">
                                Code sent to <span className="font-mono">{initInfo?.email || user?.email}</span>.
                                {ttlLeft > 0 && (
                                    <> Expires in <span className="font-mono">{mm}:{ss}</span>.</>
                                )}
                            </div>
                            <div>
                                <Label className="flex items-center gap-1.5">
                                    <Mail size={14} /> Email OTP <span className="text-[hsl(var(--destructive))]">*</span>
                                </Label>
                                <Input
                                    data-testid="delete-account-otp"
                                    inputMode="numeric"
                                    autoComplete="one-time-code"
                                    maxLength={12}
                                    value={otp}
                                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                                    placeholder="6-digit code"
                                    className="font-mono tracking-widest text-center"
                                />
                            </div>
                            <div>
                                <Label className="flex items-center gap-1.5">
                                    <KeyRound size={14} /> Account password <span className="text-[hsl(var(--destructive))]">*</span>
                                </Label>
                                <PasswordInput
                                    data-testid="delete-account-password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                />
                            </div>
                        </div>
                    )}

                    {stage === 'done' && (
                        <Alert>
                            <AlertTitle>Data erased</AlertTitle>
                            <AlertDescription>Signing you out…</AlertDescription>
                        </Alert>
                    )}

                    <DialogFooter className="flex-col-reverse sm:flex-row gap-2">
                        {stage === 'idle' && (
                            <>
                                <Button
                                    variant="outline"
                                    onClick={() => setDialogOpen(false)}
                                    data-testid="delete-account-idle-cancel"
                                >
                                    Cancel
                                </Button>
                                <Button
                                    variant="destructive"
                                    onClick={requestOtp}
                                    disabled={stage === 'busy'}
                                    className="gap-2"
                                    data-testid="delete-account-send-otp"
                                >
                                    <Mail size={15} /> Email me the code
                                </Button>
                            </>
                        )}
                        {stage === 'sent' && (
                            <>
                                <Button
                                    variant="ghost"
                                    onClick={requestOtp}
                                    disabled={ttlLeft > 540}
                                    className="sm:mr-auto"
                                    data-testid="delete-account-resend"
                                >
                                    Resend code
                                </Button>
                                <Button
                                    variant="outline"
                                    onClick={() => setDialogOpen(false)}
                                    data-testid="delete-account-sent-cancel"
                                >
                                    Cancel
                                </Button>
                                <Button
                                    variant="destructive"
                                    onClick={confirmDelete}
                                    className="gap-2"
                                    data-testid="delete-account-confirm"
                                >
                                    <Trash2 size={15} /> Permanently delete
                                </Button>
                            </>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Card>
    );
}
